/* eslint-disable no-restricted-syntax, no-await-in-loop, no-continue, no-promise-executor-return, consistent-return, no-void, max-len, class-methods-use-this, no-underscore-dangle, no-undef, no-plusplus, prefer-destructuring, no-param-reassign, no-use-before-define, no-nested-ternary */
// Ported from newdtrader (packages/core/src/_common/base/dfunded); kept in its
// original style so fixes can be carried across the two copies.
/**
 * dfunded-websocket-wrapper.js
 *
 * Wraps the DFunded funded-account core (`./core.js`) in a WebSocket-compatible
 * class so `DerivAPIBasic` (and any other consumer built against the WebSocket
 * interface) can use it transparently — mirroring `V2WrappedWebSocket` in
 * `../v2-websocket-wrapper.js` (same file layout: EventTarget scaffolding,
 * `send()` routing by request key, synthetic responses dispatched as
 * `message` events on a macrotask after `send()` returns).
 *
 * Unlike `V2WrappedWebSocket` (which transforms/normalizes requests flowing to
 * a REAL v2 Deriv endpoint), this class answers most requests itself from the
 * DFunded backend/core — trading, account, and contract-history frames never
 * reach a Deriv socket at all. Only unauthenticated market-data plumbing
 * (ticks, time, ping, one-shot history…) passes through to a real public
 * socket opened at `url`.
 *
 * @see .superpowers/sdd/task-2-brief.md
 */

const {
    getDFundedSession,
    setDFundedEmitter,
    marketRequest,
    executeDFundedPurchase,
    sellDFundedContract,
    streamContract,
    resumeOpenContracts,
    ensureRealtime,
    joinTickStream,
    stopTickPoller,
    stopAllTickPollers,
    buildFundedContract,
    synthesizeStatement,
    synthesizeProfitTable,
    orderReceipt,
    handleProposal,
    stopProposalPoller,
    stopAllProposalPollers,
    proposals,
    trackedContracts,
    terminalOrderStatuses,
    pollTicksHistory,
    pipDecimalsOf,
} = require('./core');
const { toDerivError } = require('./errors');

const { buildStubResponse, V2WrappedWebSocket } = require('../v2-websocket-wrapper');

/**
 * Synthetic `authorize` response payload for a DFunded session. Used both by
 * this wrapper's `send()` and by socket_base.js (Task 4) directly, since
 * socket_base needs the stub BEFORE the wrapper is constructed (same pattern
 * as `buildV2AuthorizeStub`).
 */
function buildDFundedAuthorizeStub() {
    const session = getDFundedSession();
    if (!session || !session.account) return {};
    const account = session.account;
    const loginid = `DFUNDED-${account.id.slice(0, 8).toUpperCase()}`;
    const currency = (account.dfunded_programs && account.dfunded_programs.currency) || 'USD';
    const balance = Number((Number(account.current_equity) - Number(account.reserved_stake)).toFixed(2));
    return {
        loginid,
        currency,
        email                        : session.email || '',
        fullname                     : 'DFunded Trader',
        balance,
        country                      : 'ke',
        is_virtual                   : 0,
        landing_company_name         : 'svg',
        landing_company_fullname     : 'Deriv (SVG) LLC',
        preferred_language           : 'EN',
        scopes                       : ['read', 'trade'],
        upgradeable_landing_companies: [],
        account_list                 : [{
            loginid,
            currency,
            is_disabled         : 0,
            is_virtual          : 0,
            landing_company_name: 'svg',
            account_type        : 'trading',
            created_at          : Math.floor(Date.now() / 1000),
        }],
        local_currencies: {},
        user_id         : 0,
    };
}

/** Requests answered from the session directly (no backend round trip). */
const balanceResponse = req => {
    const session = getDFundedSession();
    const account = session && session.account;
    const balance = account ? Number((Number(account.current_equity) - Number(account.reserved_stake)).toFixed(2)) : 0;
    const currency = (account && account.dfunded_programs && account.dfunded_programs.currency) || 'USD';
    const loginid = account ? `DFUNDED-${account.id.slice(0, 8).toUpperCase()}` : 'DFUNDED';
    const response = {
        echo_req: req,
        msg_type: 'balance',
        req_id  : req.req_id,
        balance : { balance, currency, loginid, id: 'dfunded-balance' },
    };
    if (req.subscribe) response.subscription = { id: 'dfunded-balance' };
    return response;
};

/**
 * A WebSocket-compatible class that answers Deriv-shaped requests from the
 * DFunded funded-account backend. Conforms to the WebSocket interface so
 * DerivAPIBasic and any other consumer can use it transparently.
 *
 * @param {string} url - The real public Deriv WebSocket URL, used as passthrough
 *   for unauthenticated market-data plumbing (ticks, time, ping…).
 * @param {object} authorizeStub - The synthetic `authorize` response payload to
 *   dispatch when an authorize request is intercepted — pass
 *   `buildDFundedAuthorizeStub()`.
 */
// Deriv retired the legacy `ws.derivws.com/websockets/v3` endpoint (it now
// answers HTTP 520), and the app only boots once this socket opens, so a funded
// session sat on skeleton loaders forever. Market passthrough (history, time,
// forget) now rides the v2 public socket, through the same request/response
// translation every other v2 session in this app uses.
const V2_PUBLIC_WS_URL = 'wss://api.derivws.com/trading/v1/options/ws/public';

// A refused request is retried on a fresh socket at most this often; a
// one-shot request is remembered for a retry this long.
const RECYCLE_MIN_INTERVAL_MS = 5000;
const PASSTHROUGH_TTL_MS = 20000;

// Subscription id of the "every open contract" stream (no contract_id).
const ALL_CONTRACTS_STREAM = 'dfunded-poc-all';

// Every open wrapped socket; the core's stream pushes are offered to each.
const liveSockets = new Set();

class DFundedWrappedWebSocket extends EventTarget {
    constructor(url, authorizeStub = {}) {
        super();
        this._authorizeStub = authorizeStub;
        this._tickStreamCounter = 0;
        this._pendingReal = new Map();
        // subscription key -> Map<req_id, original request>. See _dispatchSynthetic.
        this._streams = new Map();
        // Candle subscription id -> { symbol, granularity, pipSize, lastEpoch, candle }.
        // See _ohlcFromTick.
        this._candles = new Map();

        // `url` is whatever the app would have dialled (historically the legacy
        // v3 endpoint); only a v2 trading URL is honoured as given.
        const realUrl = /\/trading\/v\d+\//.test(String(url || '')) ? url : V2_PUBLIC_WS_URL;
        this._realUrl = realUrl;
        this._real = null;
        // Requests waiting for the public socket to (re)open, oldest first.
        this._queued = [];
        // req_id -> frame of every one-shot pass-through request in flight, so
        // a request Deriv refuses for rate limiting can be sent again once.
        this._passthrough = new Map();
        this._recycling = false;
        this._lastRecycleAt = 0;
        this.onopen = null;
        this.onmessage = null;
        this.onclose = null;
        this.onerror = null;
        this._openReal();

        // Wire core streams (POC pushes, ticks, balance) out as socket messages.
        // Every live socket gets them: the app can hold more than one, and each
        // only delivers the streams that were requested on it.
        liveSockets.add(this);
        setDFundedEmitter(msg => liveSockets.forEach(socket => socket._dispatchSynthetic(msg)));
        ensureRealtime();
        resumeOpenContracts();
    }

    /**
     * Dial the public socket and wire its events through. Events from a socket
     * that has since been replaced (see _recycleReal) are dropped, so a
     * deliberate replacement never looks like a lost connection to the app.
     */
    _openReal() {
        const socket = new V2WrappedWebSocket(this._realUrl, {}, { publicOnly: true });
        this._real = socket;
        const current = () => this._real === socket;

        socket.addEventListener('open', () => {
            if (!current()) return;
            this._flushQueued();
            if (this._recycling) {
                this._recycling = false;
                return; // the app never saw the old socket go
            }
            const event = new Event('open');
            this.dispatchEvent(event);
            if (typeof this.onopen === 'function') this.onopen(event);
        });

        socket.addEventListener('message', event => {
            if (!current()) return;
            let parsed;
            try {
                parsed = JSON.parse(event.data);
            } catch (_e) {
                this._forwardReal(event);
                return;
            }
            if (parsed.error?.code === 'RateLimit' && this._retryAfterRateLimit(parsed.req_id)) return;
            const pending = parsed.req_id !== undefined ? this._pendingReal.get(parsed.req_id) : undefined;
            if (pending) {
                this._pendingReal.delete(parsed.req_id);
                if (parsed.error) pending.reject(parsed.error);
                else pending.resolve(parsed);
                return;
            }
            if (parsed.req_id !== undefined) this._passthrough.delete(parsed.req_id);
            this._forwardReal(event);
        });

        socket.addEventListener('close', e => {
            if (!current()) return;
            this._recycling = false; // a replacement that never opened is a real loss
            const event = new CloseEvent('close', { code: e.code, reason: e.reason, wasClean: e.wasClean });
            this.dispatchEvent(event);
            if (typeof this.onclose === 'function') this.onclose(event);
        });

        socket.addEventListener('error', () => {
            if (!current()) return;
            const event = new Event('error');
            this.dispatchEvent(event);
            if (typeof this.onerror === 'function') this.onerror(event);
        });

        // A socket that is already open (a fake in tests) fires no open event.
        if (socket.readyState === WebSocket.OPEN) {
            this._recycling = false;
            this._flushQueued();
        }
    }

    /**
     * Deriv's request allowance is per connection. The chart asks for history
     * in bursts (every symbol, timeframe or scroll-back), and one burst too
     * many used to surface as a full-page "rate limit" interruption that only
     * a refresh cleared. Instead: replace the socket (a fresh allowance) and
     * send the refused request again, once. A second refusal is passed on.
     * Returns true when the refusal was absorbed.
     */
    _retryAfterRateLimit(reqId) {
        if (reqId === undefined) return false;
        const passthrough = this._passthrough.get(reqId);
        const pending = this._pendingReal.get(reqId);
        const entry = passthrough || pending;
        if (!entry || entry.retried) return false;
        entry.retried = true;
        this._recycleReal();
        this._queued.push(entry.frame);
        this._flushQueued();
        return true;
    }

    _recycleReal() {
        const now = Date.now();
        if (this._recycling || now - this._lastRecycleAt < RECYCLE_MIN_INTERVAL_MS) return;
        this._recycling = true;
        this._lastRecycleAt = now;
        const old = this._real;
        this._openReal();
        try {
            old.close();
        } catch (_e) {
            /* already closed */
        }
    }

    /** Send on the public socket; while it is being replaced, hold the frame for the new one. */
    _realSend(frame) {
        if (this._recycling && this._real.readyState !== WebSocket.OPEN) {
            this._queued.push(frame);
            return;
        }
        this._real.send(frame);
    }

    _flushQueued() {
        if (this._real.readyState !== WebSocket.OPEN) return;
        const frames = this._queued.splice(0);
        for (const frame of frames) this._real.send(frame);
    }

    /** A one-shot request the app sent itself, remembered until answered. */
    _sendPassthrough(frame, reqId) {
        if (reqId !== undefined) {
            this._passthrough.set(reqId, { frame, retried: false });
            setTimeout(() => this._passthrough.delete(reqId), PASSTHROUGH_TTL_MS);
        }
        this._realSend(frame);
    }

    // While the public socket is being replaced the app keeps sending; frames
    // are queued and go out on the new socket, so it is reported as open.
    get readyState() { return this._recycling ? WebSocket.OPEN : this._real.readyState; }
    get url() { return this._real.url; }
    get protocol() { return this._real.protocol; }
    get bufferedAmount() { return this._real.bufferedAmount; }
    get extensions() { return this._real.extensions; }
    get binaryType() { return this._real.binaryType; }
    set binaryType(v) { this._real.binaryType = v; }

    get CONNECTING() { return WebSocket.CONNECTING; }
    get OPEN() { return WebSocket.OPEN; }
    get CLOSING() { return WebSocket.CLOSING; }
    get CLOSED() { return WebSocket.CLOSED; }

    send(data) {
        if (typeof data !== 'string') {
            return this._real.send(data);
        }
        let parsed;
        try {
            parsed = JSON.parse(data);
        } catch (_e) {
            return this._real.send(data);
        }

        if ('authorize' in parsed) {
            this._dispatchSynthetic({
                echo_req : parsed,
                msg_type : 'authorize',
                req_id   : parsed.req_id,
                authorize: buildDFundedAuthorizeStub(),
            });
            return;
        }

        if ('balance' in parsed) {
            this._dispatchSynthetic(balanceResponse(parsed));
            return;
        }

        if ('buy' in parsed || 'buy_contract_for_multiple_accounts' in parsed) {
            this._handleBuy(parsed);
            return;
        }

        if ('sell' in parsed) {
            this._handleSell(parsed);
            return;
        }

        if ('proposal' in parsed) {
            this._handleProposal(parsed);
            return;
        }

        if ('transaction' in parsed) {
            this._handleTransactionSubscribe(parsed);
            return;
        }

        if ('proposal_open_contract' in parsed) {
            this._handleProposalOpenContract(parsed);
            return;
        }

        if ('portfolio' in parsed) {
            this._handlePortfolio(parsed);
            return;
        }

        if ('statement' in parsed || 'profit_table' in parsed) {
            this._handleSynthesize(parsed, 'statement' in parsed ? synthesizeStatement : synthesizeProfitTable);
            return;
        }

        if ('active_symbols' in parsed || 'contracts_for' in parsed || 'trading_times' in parsed) {
            this._handleMarketRequest(parsed);
            return;
        }

        if ('ticks_history' in parsed && parsed.subscribe) {
            this._handleTicksHistorySubscribe(parsed);
            return;
        }

        if ('ticks' in parsed && typeof parsed.ticks === 'string') {
            this._handleTicksSubscribe(parsed);
            return;
        }

        if ('forget' in parsed) {
            this._handleForget(parsed);
            return;
        }

        if ('forget_all' in parsed) {
            this._handleForgetAll(parsed);
            return;
        }

        const requestType = Object.keys(parsed).find(
            k => k !== 'subscribe' && k !== 'req_id' && k !== 'passthrough'
        );
        const stub = buildStubResponse(requestType, parsed);
        if (stub) {
            this._dispatchSynthetic(stub);
            return;
        }

        return this._sendPassthrough(data, parsed.req_id);
    }

    async _handleBuy(req) {
        try {
            const buyId = req.buy ?? req.buy_contract_for_multiple_accounts;
            const cached = proposals.get(String(buyId));
            const source = { ...(cached || {}), ...(req.parameters || {}) };
            const parameters = {
                ...source,
                symbol: source.symbol ?? source.underlying_symbol,
                amount: Number(req.price ?? source.amount),
            };
            const result = await executeDFundedPurchase({ price: req.price, parameters });
            this._dispatchSynthetic({ echo_req: req, msg_type: 'buy', buy: result.buy, req_id: req.req_id });
            streamContract(result.dfunded_order_id, result.dfunded_order, result.buy);
        } catch (error) {
            this._dispatchError(req, 'buy', error);
        }
    }

    async _handleSell(req) {
        try {
            const contractId = Number(req.sell);
            const settled = await sellDFundedContract(contractId);
            const receipt = orderReceipt(settled);
            const sellPrice = Number(settled.sell_price ?? 0);
            this._dispatchSynthetic({
                echo_req: req,
                msg_type: 'sell',
                req_id  : req.req_id,
                sell    : {
                    contract_id   : contractId,
                    sold_for      : sellPrice,
                    transaction_id: contractId,
                    reference_id  : Number(receipt.buy_transaction_id ?? contractId),
                },
            });
        } catch (error) {
            this._dispatchError(req, 'sell', error);
        }
    }

    async _handleProposal(req) {
        try {
            const response = await handleProposal(req);
            response.req_id = req.req_id;
            this._dispatchSynthetic(response);
        } catch (error) {
            this._dispatchError(req, 'proposal', error);
        }
    }

    /**
     * `proposal_open_contract` with no contract_id is how the portfolio follows
     * every open position (the positions drawer, Reports, chart markers and
     * the digit highlights all hang off it). Answer with the contracts we are
     * tracking, then keep it fed from each contract's stream.
     */
    _handleAllOpenContracts(req) {
        const open = [...trackedContracts.values()].filter(({ order }) => !terminalOrderStatuses.has(order.status));
        const base = { echo_req: req, msg_type: 'proposal_open_contract', req_id: req.req_id };
        const subscription = req.subscribe ? { subscription: { id: ALL_CONTRACTS_STREAM } } : {};
        if (!open.length) {
            this._dispatchSynthetic({ ...base, proposal_open_contract: {}, ...subscription });
            return;
        }
        open.forEach(({ orderId, order, buy }) => {
            this._dispatchSynthetic({
                ...base,
                proposal_open_contract: buildFundedContract(order, buy),
                ...subscription,
            });
            if (req.subscribe) streamContract(orderId, order, buy);
        });
    }

    /** Buy/sell notifications; the core pushes them, this only opens the stream. */
    _handleTransactionSubscribe(req) {
        this._dispatchSynthetic({
            echo_req   : req,
            msg_type   : 'transaction',
            req_id     : req.req_id,
            transaction: {},
            ...(req.subscribe ? { subscription: { id: 'dfunded-transaction' } } : {}),
        });
    }

    _handleProposalOpenContract(req) {
        if (req.contract_id === undefined || req.contract_id === null) {
            this._handleAllOpenContracts(req);
            return;
        }
        const contractId = Number(req.contract_id);
        const tracked = trackedContracts.get(contractId);
        const response = {
            echo_req              : req,
            msg_type              : 'proposal_open_contract',
            req_id                : req.req_id,
            proposal_open_contract: tracked ? buildFundedContract(tracked.order, tracked.buy) : {},
        };
        if (req.subscribe) response.subscription = { id: `dfunded-poc-${contractId}` };
        this._dispatchSynthetic(response);
        // Idempotent — streamContract dedupes on `streamingOrders`, so this is a
        // no-op if the contract's settlement loop is already running.
        if (tracked && req.subscribe) streamContract(tracked.orderId, tracked.order, tracked.buy);
    }

    _handlePortfolio(req) {
        const contracts = [...trackedContracts.values()]
            .filter(({ order }) => !terminalOrderStatuses.has(order.status))
            .map(({ order, buy }) => {
                const contract = buildFundedContract(order, buy);
                return {
                    contract_id   : contract.contract_id,
                    transaction_id: contract.transaction_ids?.buy,
                    longcode      : contract.longcode,
                    contract_type : contract.contract_type,
                    buy_price     : contract.buy_price,
                    payout        : contract.payout,
                    purchase_time : contract.purchase_time,
                    date_start    : contract.date_start,
                    expiry_time   : contract.date_expiry,
                    symbol        : contract.symbol,
                };
            });
        this._dispatchSynthetic({
            echo_req : req,
            msg_type : 'portfolio',
            req_id   : req.req_id,
            portfolio: { contracts },
        });
    }

    async _handleSynthesize(req, synthesizer) {
        const requestType = 'statement' in req ? 'statement' : 'profit_table';
        try {
            const response = await synthesizer(req);
            response.req_id = req.req_id;
            this._dispatchSynthetic(response);
        } catch (error) {
            this._dispatchError(req, requestType, error);
        }
    }

    async _handleMarketRequest(req) {
        const requestType = Object.keys(req).find(
            k => k !== 'subscribe' && k !== 'req_id' && k !== 'passthrough'
        );
        try {
            const response = await marketRequest(req);
            this._dispatchSynthetic({
                ...response,
                echo_req: req,
                req_id  : req.req_id,
                msg_type: response.msg_type || requestType,
            });
        } catch (error) {
            this._dispatchError(req, requestType, error);
        }
    }

    _handleTicksHistorySubscribe(req) {
        const symbol = req.ticks_history;
        const isCandles = req.style === 'candles' || req.granularity;
        const granularity = Number(req.granularity) || 60;
        const subscriptionId = isCandles
            ? `dfunded-candles-${++this._tickStreamCounter}`
            : `dfunded-ticks-${symbol}-${++this._tickStreamCounter}`;
        const oneShot = { ...req };
        delete oneShot.subscribe;

        this._sendOnRealSocket(oneShot)
            .then(response => {
                this._dispatchSynthetic({
                    ...response,
                    echo_req    : req,
                    req_id      : req.req_id,
                    subscription: { id: subscriptionId },
                });
                if (!isCandles) {
                    const times = (response.history && response.history.times) || [];
                    const lastEpoch = times.length ? Number(times[times.length - 1]) : Math.floor(Date.now() / 1000);
                    joinTickStream(subscriptionId, symbol, lastEpoch);
                    return;
                }
                // Candles: the history above is a one-shot answer, so the live
                // candle is built here from the symbol's tick stream (the same
                // feed the tick chart uses). Without it the chart drew the
                // candles up to the moment it opened and then never another:
                // the price moved, the current candle never appeared.
                const candles = response.candles || [];
                const last = candles[candles.length - 1];
                const lastOpen = last ? Number(last.epoch) : null;
                this._candles.set(subscriptionId, {
                    symbol,
                    granularity,
                    pipSize  : response.pip_size,
                    lastEpoch: lastOpen || 0,
                    candle   : last
                        ? {
                            open_time: lastOpen,
                            open     : Number(last.open),
                            high     : Number(last.high),
                            low      : Number(last.low),
                            close    : Number(last.close),
                        }
                        : null,
                });
                joinTickStream(subscriptionId, symbol, lastOpen || Math.floor(Date.now() / 1000));
            })
            .catch(error => this._dispatchError(req, 'ticks_history', error));
    }

    async _handleTicksSubscribe(req) {
        const symbol = req.ticks;
        const subscriptionId = `dfunded-ticks-${symbol}-${++this._tickStreamCounter}`;
        try {
            const response = await pollTicksHistory(symbol, 1);
            const times = (response.history && response.history.times) || [];
            const prices = (response.history && response.history.prices) || [];
            const lastIndex = times.length - 1;
            const lastEpoch = lastIndex >= 0 ? Number(times[lastIndex]) : Math.floor(Date.now() / 1000);
            const quote = lastIndex >= 0 ? Number(prices[lastIndex]) : 0;
            const pipSize = pipDecimalsOf(symbol, prices);
            this._dispatchSynthetic({
                echo_req    : req,
                msg_type    : 'tick',
                req_id      : req.req_id,
                tick        : { id: subscriptionId, symbol, epoch: lastEpoch, quote, pip_size: pipSize },
                subscription: { id: subscriptionId },
            });
            joinTickStream(subscriptionId, symbol, lastEpoch);
        } catch (error) {
            this._dispatchError(req, 'ticks', error);
        }
    }

    _handleForget(req) {
        const id = req.forget;
        if (typeof id === 'string' && id.startsWith('dfunded-')) {
            if (id.startsWith('dfunded-ticks-') || id.startsWith('dfunded-candles-')) stopTickPoller(id);
            else if (id.startsWith('dfunded-prop-')) stopProposalPoller(id);
            this._candles.delete(id);
            this._forgetStreams(
                key =>
                    key === id ||
                    (id === 'dfunded-balance' && key === 'type:balance') ||
                    (id === 'dfunded-transaction' && key === 'type:transaction')
            );
            // dfunded-poc-* / dfunded-balance have no client-owned timer to stop
            // (POC lifecycle is tied to the order; balance is realtime-pushed) —
            // the request is still acknowledged.
            this._dispatchSynthetic({ echo_req: req, msg_type: 'forget', req_id: req.req_id, forget: 1 });
            return;
        }
        this._realSend(JSON.stringify(req));
    }

    _handleForgetAll(req) {
        // Only the stream types named. This used to stop every poller on ANY
        // forget_all, and the app sends `forget_all: 'proposal'` whenever a
        // trade parameter changes, which silently killed the price stream.
        const types = new Set([].concat(req.forget_all || []));
        if (types.has('ticks') || types.has('candles')) {
            stopAllTickPollers();
            this._forgetStreams(key => key.startsWith('dfunded-ticks-') || key.startsWith('dfunded-candles-'));
            this._candles.clear();
        }
        if (types.has('proposal')) {
            stopAllProposalPollers();
            this._forgetStreams(key => key.startsWith('dfunded-prop-'));
        }
        if (types.has('proposal_open_contract')) this._forgetStreams(key => key.startsWith('dfunded-poc-'));
        if (types.has('balance')) this._forgetStreams(key => key === 'type:balance' || key === 'dfunded-balance');
        if (types.has('transaction')) {
            this._forgetStreams(key => key === 'type:transaction' || key === 'dfunded-transaction');
        }
        // No dfunded-owned subscription types are left unhandled above, but the
        // real socket may still hold passthrough subscriptions (plain ticks/time)
        // — forward so those get cleared too; its own forget_all response flows
        // back through the generic passthrough listener.
        this._realSend(JSON.stringify(req));
    }

    /** Send a request on the real socket and resolve with its correlated
     * (by req_id) response, without exposing that raw response to consumers —
     * used to enrich one-shot passthrough answers (e.g. ticks_history) before
     * dispatching them as our own synthetic message. */
    _sendOnRealSocket(req) {
        return new Promise((resolve, reject) => {
            const reqId = req.req_id;
            const frame = JSON.stringify(req);
            this._pendingReal.set(reqId, { resolve, reject, frame, retried: false });
            setTimeout(() => {
                if (this._pendingReal.delete(reqId)) reject(new Error('DFunded passthrough request timed out'));
            }, 15000);
            this._realSend(frame);
        });
    }

    _forwardReal(event) {
        const newEvent = new MessageEvent('message', { data: event.data });
        this.dispatchEvent(newEvent);
        if (typeof this.onmessage === 'function') this.onmessage(newEvent);
    }

    /**
     * Deriv stamps EVERY message of a subscription with the req_id of the
     * request that opened it, and the app's API layer (deriv-api / api-v2)
     * delivers stream messages by that req_id. The core's stream pushes (ticks,
     * quotes, open-contract updates, balance, transactions) are produced
     * outside any request, so without this they reached nobody: the chart got
     * its initial history and then sat still.
     *
     * The first response of a subscription registers its request; later pushes
     * for the same stream are delivered once per registered request, stamped
     * with that request's req_id and echo_req.
     */
    _streamKeys(payload) {
        const keys = [];
        if (payload.subscription && payload.subscription.id) keys.push(payload.subscription.id);
        // The portfolio subscribes to ALL open contracts with one request that
        // names no contract; every contract update belongs to that stream too.
        if (payload.msg_type === 'proposal_open_contract') keys.push(ALL_CONTRACTS_STREAM);
        if (payload.msg_type === 'balance' || payload.msg_type === 'transaction') keys.push(`type:${payload.msg_type}`);
        return [...new Set(keys)];
    }

    _registerStream(payload) {
        const req = payload.echo_req;
        if (payload.req_id === undefined || !req || !req.subscribe || payload.error) return;
        const subscriptionId = payload.subscription && payload.subscription.id;
        // A request registers under ITS OWN stream: a single-contract subscriber
        // must not start receiving every other contract. Balance/transaction
        // pushes carry no subscription id, so those also register by type.
        const keys = this._streamKeys(payload).filter(
            key => key !== ALL_CONTRACTS_STREAM || subscriptionId === ALL_CONTRACTS_STREAM
        );
        keys.forEach(key => {
            if (!this._streams.has(key)) this._streams.set(key, new Map());
            this._streams.get(key).set(payload.req_id, { req, subscriptionId });
        });
    }

    _forgetStreams(predicate) {
        [...this._streams.keys()].filter(predicate).forEach(key => this._streams.delete(key));
    }

    /**
     * A tick pushed under a candle subscription, as the `ohlc` message Deriv
     * sends on a candles stream: the candle the tick falls in, updated by it.
     * A tick from a later period opens a new candle; an older tick (a feed
     * catching up) changes nothing. Null when there is nothing to send.
     */
    _ohlcFromTick(subscriptionId, state, tick) {
        const epoch = Number(tick && tick.epoch);
        const quote = Number(tick && tick.quote);
        if (!Number.isFinite(epoch) || !Number.isFinite(quote) || epoch < state.lastEpoch) return null;
        const openTime = epoch - (epoch % state.granularity);
        let candle = state.candle;
        if (candle && openTime < candle.open_time) return null;
        if (!candle || openTime > candle.open_time) {
            candle = { open_time: openTime, open: quote, high: quote, low: quote, close: quote };
            state.candle = candle;
        } else {
            candle.high = Math.max(candle.high, quote);
            candle.low = Math.min(candle.low, quote);
            candle.close = quote;
        }
        state.lastEpoch = epoch;
        const pipSize = Number.isInteger(tick.pip_size) ? tick.pip_size : state.pipSize;
        if (Number.isInteger(pipSize)) state.pipSize = pipSize;
        const price = value => (Number.isInteger(pipSize) ? value.toFixed(pipSize) : String(value));
        return {
            msg_type: 'ohlc',
            ohlc    : {
                id         : subscriptionId,
                symbol     : state.symbol,
                granularity: state.granularity,
                open_time  : candle.open_time,
                epoch,
                open       : price(candle.open),
                high       : price(candle.high),
                low        : price(candle.low),
                close      : price(candle.close),
                ...(Number.isInteger(pipSize) ? { pip_size: pipSize } : {}),
            },
            subscription: { id: subscriptionId },
        };
    }

    _dispatchSynthetic(message) {
        let payload = message;
        if (payload.req_id === undefined && payload.msg_type === 'tick') {
            const streamId = payload.subscription && payload.subscription.id;
            const state = streamId && this._candles.get(streamId);
            if (state) {
                payload = this._ohlcFromTick(streamId, state, payload.tick);
                if (!payload) return;
            }
        }
        if (payload.req_id !== undefined) {
            this._registerStream(payload);
            this._deliver(payload);
            return;
        }
        // Every request subscribed to any stream this push belongs to, once each.
        const requests = new Map();
        this._streamKeys(payload).forEach(key => {
            (this._streams.get(key) || new Map()).forEach((entry, reqId) => requests.set(reqId, entry));
        });
        if (!requests.size) {
            // Not a stream this socket asked for (or not a stream at all).
            if (!payload.subscription && payload.msg_type !== 'transaction') this._deliver(payload);
            return;
        }
        requests.forEach((entry, reqId) =>
            this._deliver({
                ...payload,
                echo_req: entry.req,
                req_id  : reqId,
                ...(entry.subscriptionId ? { subscription: { id: entry.subscriptionId } } : {}),
            })
        );
    }

    _deliver(payload) {
        setTimeout(() => {
            const event = new MessageEvent('message', { data: JSON.stringify(payload) });
            this.dispatchEvent(event);
            if (typeof this.onmessage === 'function') this.onmessage(event);
        }, 0);
    }

    _dispatchError(req, msgType, error) {
        this._dispatchSynthetic({
            req_id  : req.req_id,
            echo_req: req,
            error   : toDerivError(error),
            msg_type: msgType,
        });
    }

    close(code, reason) {
        // Tear down the core's background pollers (ticks_history + proposal
        // refresh intervals) BEFORE closing the real socket — otherwise they
        // keep firing (and, for proposals, still calling emitAll) against a
        // consumer that has already torn down.
        stopAllTickPollers();
        stopAllProposalPollers();
        liveSockets.delete(this);
        this._streams.clear();
        this._candles.clear();
        return this._real.close(code, reason);
    }
}

module.exports = {
    DFundedWrappedWebSocket,
    buildDFundedAuthorizeStub,
};
